import { BILLING_FUNCTION_PATH, BILLING_PROTOCOL_VERSION, checkBillingResponse, type BillingOkResponse, type InspectionHolder } from '../../supabase/functions/_shared/billingPolicy';
import { devPurchaseWorld } from '../native/fakePurchases';
import { getSupabase } from './supabaseAdapter';

// Client transport to the billing function (supabase/functions/billing-v2). Same discipline as
// src/backend/aiClient.ts:
//   - the request carries the signed-in user's JWT; no store secret exists in this bundle;
//   - every outcome is normalized to a code the UI can translate; a raw server message is never
//     shown to an inspector;
//   - nothing here decides anything about money. The server owns the allowance (Owner decision
//     2026-09-11 X-7) and this module only carries its answer.
export type BillingFailureCode = 'offline' | 'network' | 'timeout' | 'notConfigured' | 'unauthenticated' | 'billingUnavailable' | 'malformedResponse' | 'serverError';

export type BillingCallOutcome = { kind: 'ok'; response: BillingOkResponse } | { kind: 'failed'; code: BillingFailureCode; message: string };

export interface BillingConsumeBody {
  inspectionId: string;
  clientCreatedAt: number | null;
  offline: boolean;
  // The device creating it (billingPolicy.ts V-2).
  device?: InspectionHolder | null;
}

export interface BillingTransport {
  status(token: string | null): Promise<BillingCallOutcome>;
  consume(token: string | null, body: BillingConsumeBody): Promise<BillingCallOutcome>;
  // Owner decision list 2026-10-19 item 46: a redemption code (web build only), with the consent tick.
  redeem(token: string | null, body: { code: string; consent: true }): Promise<BillingCallOutcome>;
  // Owner 2026-10-20 (billingPolicy.ts V-1 … V-3): an unfinished inspection deleted on this device, its final
  // report generated, a file that carried it to this device.
  release(token: string | null, body: { inspectionId: string; device: InspectionHolder }): Promise<BillingCallOutcome>;
  close(token: string | null, body: { inspectionId: string }): Promise<BillingCallOutcome>;
  hold(token: string | null, body: { inspectionId: string; device: InspectionHolder }): Promise<BillingCallOutcome>;
}

export const BILLING_CLIENT_TIMEOUT_MS = 15_000;

export function billingEndpoint(): string | null {
  const base = ((import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '').trim().replace(/\/$/, '');
  return base.length > 0 ? `${base}${BILLING_FUNCTION_PATH}` : null;
}

export function billingBackendConfigured(): boolean {
  return billingEndpoint() !== null && ((import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '').trim().length > 0;
}

function online(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
}

async function call(path: 'status' | 'consume' | 'redeem' | 'release' | 'close' | 'hold', token: string | null, body: Record<string, unknown>): Promise<BillingCallOutcome> {
  const endpoint = billingEndpoint();
  const anon = ((import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '').trim();
  if (!endpoint || anon.length === 0) return { kind: 'failed', code: 'notConfigured', message: 'billing backend is not configured' };
  if (!online()) return { kind: 'failed', code: 'offline', message: 'device is offline' };
  if (!token) return { kind: 'failed', code: 'unauthenticated', message: 'no session token' };
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), BILLING_CLIENT_TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', apikey: anon, Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action: path, protocolVersion: BILLING_PROTOCOL_VERSION, ...body }),
      signal: controller.signal
    });
  } catch (raw) {
    clearTimeout(timer);
    const aborted = raw instanceof Error && raw.name === 'AbortError';
    return { kind: 'failed', code: aborted ? 'timeout' : 'network', message: aborted ? 'billing request timed out' : 'network error' };
  }
  clearTimeout(timer);
  let parsed: unknown = null;
  try {
    parsed = (await res.json()) as unknown;
  } catch {
    parsed = null;
  }
  const checked = checkBillingResponse(parsed);
  if (!checked) {
    // A gateway or platform answer that is not our protocol (a 404 when the function is not
    // deployed, a 5xx HTML page). Never shown raw.
    return { kind: 'failed', code: res.status === 404 ? 'notConfigured' : 'malformedResponse', message: `unexpected answer (HTTP ${res.status})` };
  }
  if (!checked.ok) {
    if (checked.code === 'unauthenticated') return { kind: 'failed', code: 'unauthenticated', message: checked.message };
    if (checked.code === 'billingUnavailable') return { kind: 'failed', code: 'billingUnavailable', message: checked.message };
    return { kind: 'failed', code: 'serverError', message: checked.message };
  }
  return { kind: 'ok', response: checked };
}

export const fetchBillingTransport: BillingTransport = {
  status: (token) => call('status', token, {}),
  consume: (token, body) => call('consume', token, { inspectionId: body.inspectionId, clientCreatedAt: body.clientCreatedAt, offline: body.offline, device: body.device ?? null }),
  redeem: (token, body) => call('redeem', token, { code: body.code, consent: body.consent }),
  release: (token, body) => call('release', token, { inspectionId: body.inspectionId, device: body.device }),
  close: (token, body) => call('close', token, { inspectionId: body.inspectionId }),
  hold: (token, body) => call('hold', token, { inspectionId: body.inspectionId, device: body.device })
};

const notConfigured = async (): Promise<BillingCallOutcome> => ({ kind: 'failed', code: 'notConfigured', message: 'billing backend is not configured' });

// A host with no billing backend at all (a production web preview): says so instead of pretending.
export const unavailableBillingTransport: BillingTransport = {
  status: notConfigured,
  consume: notConfigured,
  redeem: notConfigured,
  release: notConfigured,
  close: notConfigured,
  hold: notConfigured
};

let transportOverride: BillingTransport | null = null;

export function setBillingTransportForTests(transport: BillingTransport | null): void {
  transportOverride = transport;
}

export function getBillingTransport(): BillingTransport {
  if (transportOverride) return transportOverride;
  if (billingBackendConfigured()) return fetchBillingTransport;
  // DEVELOPMENT ONLY. The browser dev server has no billing backend, so the plan page runs against
  // the same simulation the fake store uses and the flows can be exercised end to end.
  // import.meta.env.DEV is false in every shipped build, so this branch cannot grant anything to a
  // real user; a production web host falls through to "not configured", which the UI states plainly.
  if (import.meta.env.DEV) return devPurchaseWorld().transport;
  return unavailableBillingTransport;
}

// ---------- identity ----------

export interface BillingIdentity {
  // The Supabase user id — also the RevenueCat app user id, so a purchase can only ever land on the
  // account that made it (memory yanwushi-auth-gate §2).
  userId: string;
  email: string | null;
}

let identityForTests: BillingIdentity | null = null;

export function setBillingIdentityForTests(identity: BillingIdentity | null): void {
  identityForTests = identity;
}

export async function currentBillingIdentity(): Promise<BillingIdentity | null> {
  if (identityForTests) return identityForTests;
  const client = getSupabase();
  if (!client) return null;
  try {
    const { data } = await client.auth.getSession();
    const user = data.session?.user;
    return user ? { userId: user.id, email: user.email ?? null } : null;
  } catch {
    return null;
  }
}
